import { PropsWithChildren } from 'react';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import {
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarSpace,
  useSidebarOpenState,
  Link,
} from '@backstage/core-components';
import { SidebarSearchModal } from '@backstage/plugin-search';
import { MyGroupsSidebarItem } from '@backstage/plugin-org';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { Config } from '@backstage/config';
import { NotificationsSidebarItem } from '@backstage/plugin-notifications';
import { makeStyles } from '@material-ui/core';
import CategroyIcon from '@material-ui/icons/Category';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import ExtensionIcon from '@material-ui/icons/Extension';
import HomeIcon from '@material-ui/icons/Home';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import GroupIcon from '@material-ui/icons/People';
import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';
import PlattLogoFull from './PlattLogoFull';
import PlattLogoIcon from './PlattLogoIcon';
import ImageLogoFull from './ImageLogoFull';
import ImageLogoIcon from './ImageLogoIcon';

const useSidebarLogoStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 24,
  },
});

export type SidebarLogoProps = {
  logoFull?: JSX.Element;
  logoIcon?: JSX.Element;  
  usePlattLogo?: boolean;
};

const chooseLogoFull = (config: Config, props: SidebarLogoProps) => {
  const url = config.getOptionalString('platt.logo.fullUrl');
  if (url) {
    return <ImageLogoFull url={url} />;
  }
  const usePlattLogo = props?.usePlattLogo ?? true;
  return props?.logoFull ?? (usePlattLogo ? <PlattLogoFull /> : <LogoFull />); 
}

const chooseLogoIcon = (config: Config, props: SidebarLogoProps) => {
  const url = config.getOptionalString('platt.logo.iconUrl');
  if (url) {
    return <ImageLogoIcon url={url} />;
  }
  const usePlattLogo = props?.usePlattLogo ?? true;
  return props?.logoIcon ?? (usePlattLogo ? <PlattLogoIcon /> : <LogoIcon />); 
}

const SidebarLogo = (props: SidebarLogoProps) => {
  const config = useApi(configApiRef);
  const classes = useSidebarLogoStyles();

  const { isOpen } = useSidebarOpenState();

  const logoFull = chooseLogoFull(config, props); 
  const logoIcon = chooseLogoIcon(config, props); 
  
  return (
    <div className={classes.root}>
      <Link to="/" underline="none" className={classes.link} aria-label="Home">
        {isOpen ? logoFull : logoIcon}
      </Link>
    </div>
  );
};

export type SideMenuProps = PropsWithChildren<{
  additionalMenuItems?: React.ReactNode;
  sidebarLogo?: SidebarLogoProps;
}>;
export const Root = (props: SideMenuProps) => (
  <SidebarPage>
    <Sidebar>
      <SidebarLogo
        logoFull={props.sidebarLogo?.logoFull}
        logoIcon={props.sidebarLogo?.logoIcon}
        usePlattLogo={props.sidebarLogo?.usePlattLogo}
      />
      <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
        <SidebarSearchModal />
      </SidebarGroup>
      <SidebarDivider />
      <SidebarGroup label="Menu" icon={<MenuIcon />}>
        {/* Global nav, not org-specific */}
        <SidebarItem icon={HomeIcon} to="/" text="Home" />
        <SidebarItem icon={CategroyIcon} to="catalog" text="Catalog" />
        <SidebarItem icon={ExtensionIcon} to="api-docs" text="APIs" />
        <SidebarItem icon={LibraryBooks} to="docs" text="Docs" />
        <MyGroupsSidebarItem
          singularTitle="My Group"
          pluralTitle="My Groups"
          icon={GroupIcon}
        />
        <SidebarItem icon={CreateComponentIcon} to="create" text="Create..." />
        {/* End global nav */}
        <SidebarDivider />
      </SidebarGroup>
      {props.additionalMenuItems}
      <SidebarSpace />
      <SidebarDivider />
      <NotificationsSidebarItem />
      <SidebarDivider />      
      <SidebarGroup
        label="Settings"
        icon={<UserSettingsSignInAvatar />}
        to="/settings"
      >
        <SidebarSettings />
      </SidebarGroup>
    </Sidebar>
    {props?.children}
  </SidebarPage>
);
